import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
	Button,
	ButtonGroup,
	ClickAwayListener,
	Grid,
	Grow,
	MenuItem,
	MenuList,
	Paper,
	Popper,
	styled,
	Typography
} from '@mui/material';

import { type PaginatorType } from '../hooks/usePaginator';
import useSettings from '../hooks/useSettings';
import Icon from '../Icon';

const PaginationInfo = styled(Button)(({ theme }) => ({
	height: 36,
	'&.MuiButtonGroup-grouped:not(:last-of-type)': {
		border: 'none'
	}
}));

const PaginationButton = styled(Button)(({ theme }) => ({
	paddingLeft: 0,
	paddingRight: 0,
	'&.MuiButtonGroup-grouped:not(:last-of-type)': {
		border: 'none'
	}
}));

/** The pagination component used in almost every list */
function Pagination({ maxRows, nPage, setNPage, nRows, setNRows }: PaginatorType) {
	const anchorRef = React.useRef(null);
	const { t } = useSettings();
	const intl = useIntl();

	const maxPages = Math.ceil(maxRows / nRows);
	const maxVisibleRows = nPage * nRows;
	const pageFromRows = maxRows > 0 ? (nPage - 1) * nRows + 1 : 0;
	const pageToRows = maxVisibleRows > maxRows ? maxRows : maxVisibleRows;

	const [rowMenuOpen, setRowMenuOpen] = React.useState(false);

	const handleChangeRows = event => {
		setNRows(event.target.value);
		setNPage(1);
		setRowMenuOpen(false);
	};

	const handleChangePage = (page: number) => {
		setNPage(page);
	};

	const RowCountMenuListItem = ({ value }: { value: number }) => (
		<MenuItem value={value} selected={nRows === value} onClick={handleChangeRows}>
			<FormattedMessage id="pagination.rowsCount" defaultMessage={t('PAGINATOR_ROWS_COUNT')} values={{ value }} />
		</MenuItem>
	);

	return (
		<Grid container justifyContent="flex-end" alignItems="center">
			<Grid>
				<PaginationInfo
					ref={anchorRef}
					className="Pagination-info"
					color="inherit"
					onClick={() => setRowMenuOpen(true)}
				>
					<Typography className="text-14" variant="subtitle2">
						<FormattedMessage
							id="pagination.displayedRows"
							defaultMessage={t('PAGINATOR_ROWS_DISPLAYED')}
							values={{
								from: intl.formatNumber(pageFromRows),
								to: intl.formatNumber(pageToRows),
								total: intl.formatNumber(maxRows)
							}}
						/>
					</Typography>
				</PaginationInfo>
			</Grid>
			<Grid>
				<ButtonGroup className="h-36" variant="text">
					<PaginationButton color="inherit" onClick={() => handleChangePage(1)} disabled={nPage === 1}>
						<Icon className="align-middle" icon="page-first" />
					</PaginationButton>
					<PaginationButton
						color="inherit"
						onClick={() => handleChangePage(nPage - 1)}
						disabled={nPage === 1}
					>
						<Icon className="align-middle" icon="chevron-left" />
					</PaginationButton>
					<PaginationButton
						color="inherit"
						onClick={() => handleChangePage(nPage + 1)}
						disabled={nPage >= maxPages}
					>
						<Icon className="align-middle" icon="chevron-right" />
					</PaginationButton>
					<PaginationButton
						color="inherit"
						onClick={() => handleChangePage(maxPages)}
						disabled={nPage >= maxPages}
					>
						<Icon className="align-middle" icon="page-last" />
					</PaginationButton>
				</ButtonGroup>
			</Grid>
			<Popper open={rowMenuOpen} anchorEl={anchorRef.current} placement="bottom" transition disablePortal>
				{({ TransitionProps, placement }) => (
					<Grow
						{...TransitionProps}
						style={{ transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom' }}
					>
						<Paper>
							<ClickAwayListener onClickAway={() => setRowMenuOpen(false)}>
								<MenuList>
									<RowCountMenuListItem value={15} />
									<RowCountMenuListItem value={30} />
									<RowCountMenuListItem value={50} />
									<RowCountMenuListItem value={100} />
								</MenuList>
							</ClickAwayListener>
						</Paper>
					</Grow>
				)}
			</Popper>
		</Grid>
	);
}

export default React.memo(Pagination);
